/** @jsxImportSource @emotion/react */

import { css, Global, ThemeProvider, useTheme } from '@emotion/react';
import { CartProvider } from '@shared/cart-react';
import { COUNTRY } from '@shared/cove-ui';
import { ProductsProvider } from '@shared/react-product';
import { GlobalThemeProvider } from '@shared/react-ui-core';
import PropTypes from 'prop-types';
import { CRAButton, CRALink } from '../components/cra/Link';
import theme from '../theme';
import { AuthProvider } from './auth';
import { BreadcrumbsProvider } from './breadcrumbs';
import { ClientProvider } from './client';
import datoLoad from './datoLoad';
import { QueryProvider } from './query-context';
import { ToastProvider } from './toast';

const Globals = () => {
  const { fonts } = useTheme();
  return (
    <Global
      styles={css`
        *,
        *:before,
        *:after {
          box-sizing: inherit;
        }
        h1 {
          font-family: ${fonts.primary};
        }
        html,
        body {
          font-style: normal;
          padding: 0;
          margin: 0;
        }
      `}
    />
  );
};

const partnerEnv =
  (typeof window !== 'undefined' && process.env.NODE_ENV) || 'development';

const AppProviders = (__dp) => {
  const { children } = {
    ...{
      children: null
    },
    ...__dp
  };
  const apiEnv = process.env.NODE_ENV || 'development';
  const partnerId = apiEnv !== 'production' ? '39770523' : '6400414';
  const datoToken = process.env.DATO_TOKEN || '7c8a71c5a466ac293a9f9f29233e78';
  const preview = apiEnv !== 'production';

  return (
    <GlobalThemeProvider
      overwriteTheme={{
        components: {
          MuiButtonBase: {
            defaultProps: {
              LinkComponent: CRAButton
            }
          },
          MuiLink: {
            defaultProps: {
              component: CRALink
            }
          }
        }
      }}
    >
      <ThemeProvider theme={theme}>
        <Globals />
        <AuthProvider>
          <BreadcrumbsProvider>
            <QueryProvider>
              <CartProvider
                apiV4Url={process.env.REACT_APP_COVE_API_V4_URL}
                datoData={datoLoad()}
                prefixLocalStorageKey="leads/"
                country={COUNTRY}
              >
                <ProductsProvider
                  partnerId={partnerId}
                  datoToken={datoToken}
                  preview={preview}
                >
                  <ClientProvider>
                    <ToastProvider>{children}</ToastProvider>
                  </ClientProvider>
                </ProductsProvider>
              </CartProvider>
            </QueryProvider>
          </BreadcrumbsProvider>
        </AuthProvider>
      </ThemeProvider>
    </GlobalThemeProvider>
  );
};

AppProviders.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ])
};

export default AppProviders;
